import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import { type Security, safeAbsurd } from '@ipfabric/utils';
import classNames from 'classnames';
import { renderSecurityValueEnsureDereferenced } from 'components/SecuritySchema/helpers/renderSecurityValue';
import type {
  PathLookupOptionsNode,
  SecuritySchemaNodeComponentProps,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, Fragment } from 'react';

export const CPathLookupOptionsNode: FC<
  SecuritySchemaNodeComponentProps<PathLookupOptionsNode>
> = ({ node, context }) => {
  const msg = useLocale();
  const simpleTestMsg = msg.securitySchema.test.simple;
  const pathLookupOptionsMsg = msg.securitySchema.pathLookupOptions;
  const renderOption = (option: Security.PathLookupOption) => {
    if (option.original) {
      return option.original;
    }
    switch (option.type) {
      case 'natIpv4Translated':
      case 'natIpv6Translated':
        return (
          <>
            {`${option.header}.${option.field}: `}
            {renderSecurityValueEnsureDereferenced(simpleTestMsg, option.value)}
          </>
        );
      case 'natIpv4L3IntPrimaryIp':
      case 'natIpv6L3IntPrimaryIp':
        return `interface.src: ${option.value}`;
      case 'natIpv4RoutingOutL3IntPrimaryIp':
      case 'natIpv6RoutingOutL3IntPrimaryIp':
        return `interface.src: (${pathLookupOptionsMsg.definedInRouting})`;
      case 'natIpv4LoadBalancingDnat':
        return option.value.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: no better key there
          <Fragment key={index}>
            {index > 0 && <br />}
            {renderSecurityValueEnsureDereferenced(simpleTestMsg, item.toIp)}
            {item.toPort
              ? `:${renderSecurityValueEnsureDereferenced(simpleTestMsg, item.toPort)}`
              : null}
          </Fragment>
        ));
      case 'nat46StatelessToPrefix':
        return `${option.header}.${option.field}: stateless NAT46 (prefix ${option.value})`;
      case 'nat64StatelessDstFromPrefix':
        return `${option.header}.${option.field}: stateless NAT64`;
      //  NIM-9326: above condition `if(option.original) return ...` makes below code unreachable/useless
      case 'pbrInterface':
      case 'pbrInterfaceDefault':
      case 'pbrIpv4Nexthops':
      case 'pbrIpv4NexthopsDefault':
      case 'pbrIpv4NexthopsRecursive':
      case 'pbrIpv4NexthopsVerifyAvailability':
      case 'pbrMacNexthops':
        return `PBR: ${option.type}, original: ${option.original}`;
      case 'pbrVrf':
        return `PBR: ${option.type}, value: ${option.value}`;
      default:
        safeAbsurd(option);
        return 'Unknown option';
    }
  };
  return (
    <div
      className={classNames(
        'security-tree-node security-tree-node--path-lookup-options',
        context.highlightedSubtree && 'security-tree-node--highlighted',
      )}
    >
      {node.pathLookupOptions.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: no better key there
        <TextOverflowTooltip as='p' key={index}>
          {renderOption(item)}
        </TextOverflowTooltip>
      ))}
    </div>
  );
};
